import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

/**
 * Handles the Emergent Google OAuth redirect back to the app.
 * The auth service returns to `/auth/callback#session_id=<id>` on our domain.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [status, setStatus] = useState("Verifying your Google sign-in…");

  useEffect(() => {
    const hash = window.location.hash || "";
    const sessionId = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    if (!sessionId) {
      toast.error("Missing Google session id");
      navigate("/login/bank", { replace: true });
      return;
    }
    (async () => {
      try {
        const { data } = await api.post(
          "/auth/google/callback",
          { session_id: sessionId },
          { withCredentials: true }
        );
        if (data.status === "signed_in") {
          setUser(data.user);
          toast.success(`Welcome, ${data.user.name}`);
          navigate(`/${data.user.role}`, { replace: true });
          return;
        }
        // needs_signup — send to completion form
        sessionStorage.setItem("google_pending", JSON.stringify(data.google));
        navigate("/signup/google/bank", { replace: true });
      } catch (e) {
        toast.error(formatApiError(e));
        navigate("/login/bank", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600" />
        <p className="mt-4 text-sm text-slate-600" data-testid="auth-callback-status">{status}</p>
      </div>
    </div>
  );
}
